import React from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Button,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Stack,
} from "@mui/material";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SecurityIcon from "@mui/icons-material/Security";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ReportIcon from "@mui/icons-material/Report";
import WarningIcon from "@mui/icons-material/Warning";
import GppGoodIcon from "@mui/icons-material/GppGood";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../Navbar";
import HomeFooter from '../HomeFooter.jsx';

const ICleverGate = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: "About Lider SecureGate",
      image: "img1.png",
      description: "Lider SecureGate is an Android application and access management system designed for efficient and secure Entry/Exit management with additional services. The application automates gate check-in and out procedures, provides induction training to drivers and enforces safety protocols.",
      features: [
        "Verifies driver identity and license validity automatically",
        "Detects alcohol influence through integrated sensors",
        "Tracks traffic rule violations and past incidents",
        "Ensures only responsible and law-abiding drivers are granted access",
        "Provides emergency contact information for immediate assistance",
        "Comprehensive solution for terminal safety and security"
      ]
    },
    {
      title: "SECURITY ENTRY GATE",
      image: "Reports.jpg",
      description: "Advanced entry gate system with multiple verification methods to ensure secure and efficient driver authentication.",
      methods: [
        {
          icon: <QrCodeScannerIcon />,
          title: "SCAN PHOTO",
          description: "Security officer scans driving license to automatically fetch driver's name and DL number"
        },
        {
          icon: <PhotoLibraryIcon />,
          title: "GALLERY OPTION",
          description: "Fetch driver's name and DL number from uploaded driving license photos"
        },
        {
          icon: <KeyboardIcon />,
          title: "MANUAL ENTRY",
          description: "Manual data entry option for special cases"
        },
        {
          icon: <GppGoodIcon />,
          title: "USER ID",
          description: "Automatically fetch name, DL number and mobile number from database"
        }
      ]
    },
    {
      title: "SECURITY EXIT GATE",
      image: "security entry gate.jpg",
      description: "Comprehensive exit management system with multiple verification options for vechicle departure.",
      methods: [
        {
          icon: <QrCodeScannerIcon />,
          title: "SCAN PHOTO",
          description: "Scan driving license to retrieve vechicle entry gate details"
        },
        {
          icon: <PhotoLibraryIcon />,
          title: "GALLERY",
          description: "Retrieve vechicle entry details from uploaded license photos"
        },
        {
          icon: <KeyboardIcon />,
          title: "MANUAL ENTRY",
          description: "Manual selection of vechicle entry details"
        },
        {
          icon: <SearchIcon />,
          title: "SEARCH",
          description: "Find vechicle entry details using reference number sent to driver's mobile"
        }
      ]
    },
    {
      title: "VIOLATION MANAGEMENT",
      image: "security ex gate.jpg",
      description: "Comprehensive violation tracking and management system to ensure driver accountability and safety compliance.",
      cards: [
        {
          color: "grey",
          title: "GREY CARD",
          description: "First warning for minor violations"
        },
        {
          color: "#FFD700",
          title: "YELLOW CARD",
          description: "Second warning for moderate violations"
        },
        {
          color: "#FF0000",
          title: "RED CARD",
          description: "Final warning - Driver access revoked"
        }
      ],
      features: [
        "Driver details tracking (name, DL number)",
        "Date, case issue and reason of violation recorded",
        "Filter reports by daily, weekly, monthly, yearly",
        "Digital storage of all violations",
        "Real-time notification to drivers"
      ]
    },
    {
      title: "REPORTS & ANALYTICS",
      image: "pathway.jpg",
      description: "Comprehensive reporting system for all safety and compliance data with advanced filtering capabilities.",
      reportTypes: [
        "Induction Training Reports",
        "Violation Reports",
        "Incident/Accident Reports",
        "Twist Lock Arrestor Reports",
        "Safety Jacket Reports",
        "Long Standing Reports"
      ],
      benefits: [
        "Improved safety through protocol compliance",
        "Regulatory compliance documentation",
        "Easy access to historical data",
        "Time-based filtering (daily/weekly/monthly/yearly)",
        "Centralized data storage and management"
      ]
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "backOut"
      }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box sx={{
      backgroundColor: '#f0f4f8',
      minHeight: '100vh',
      overflow: 'hidden'
    }}>
      < Navbar />

      {/* Hero Section - LEFT ALIGNED */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        sx={{
          position: 'relative',
          py: { xs: 10, sm: 12, md: 15 },
          textAlign: 'left',
          color: '#0f172a',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)',
        }}
      >
        {/* Animated Background Elements */}
        <Box
          component={motion.div}
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(69,81,190,0.1) 0%, transparent 70%)',
          }}
        />
        <Box
          component={motion.div}
          animate={{ y: [0, 20, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          sx={{
            position: 'absolute',
            bottom: '15%',
            right: '10%',
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(69,81,190,0.05) 0%, transparent 70%)',
          }}
        />

        <Container
          maxWidth={false}
          sx={{
            position: 'relative',
            zIndex: 2,
            px: { xs: 3, sm: 4, md: 6, lg: 10, xl: 14 },
          }}
        >
          <Box
            component={motion.div}
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              textAlign: 'left',
            }}
          >
            <Chip
              label="INNOVATIVE SECURITY SOLUTION"
              color="primary"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                fontSize: '0.9rem',
                py: 1,
                px: 2,
                background: 'rgba(69,81,190,0.1)',
                color: '#4551be',
                border: '1px solid rgba(69,81,190,0.2)'
              }}
            />

            <Stack
  direction="row"
  alignItems="center"
  justifyContent="flex-start"
  spacing={2}
  sx={{ mb: 3 }}
>
  {/* Logo */}
  <Box
    sx={{
      width: { xs: 95, md: 140 },
      height: { xs: 75, md: 110 },
      borderRadius: 2,
      bgcolor: '#fff',
      border: '1px solid rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      flexShrink: 0,
      p: 0,
    }}
  >
    <Box
      component="img"
      src="/images/SecureGate.png"
      alt="Lider SecureGate Logo"
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }}
    />
  </Box>

  {/* Title */}
  <Typography
    variant="h1"
    sx={{
      fontWeight: 900,
      fontSize: {
        xs: '2.5rem',
        sm: '3.5rem',
        md: '4.5rem',
      },
      color: '#4551be',
      lineHeight: 1,
    }}
  >
    Lider SecureGate
  </Typography>
</Stack>

            <Typography
              variant="h4"
              sx={{
                mb: 4,
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                opacity: 0.9,
                maxWidth: '800px',
                color: '#475569',
                mx: 0,
                lineHeight: 1.6
              }}
            >
              Advanced Android-based Access Management System for Secure Terminal Operations
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Sections */}
      <Box sx={{
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        py: 8,
        background: '#f8fafc'
      }}>
        <Box sx={{
          width: '100%',
          px: { xs: 4, sm: 6, md: 8, lg: 12, xl: 16 },
          margin: '0 auto'
        }}>
          {sections.map((section, index) => (
            <Box key={index} sx={{ mb: { xs: 10, md: 15 } }}>
              {/* Section Title */}
              <Box
                component={motion.div}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                sx={{
                  textAlign: 'center',
                  mb: 6,
                  position: 'relative'
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    color: '#4551be',
                    fontSize: {
                      xs: '2rem',
                      md: '2.5rem',
                      lg: '3rem'
                    },
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2
                  }}
                >
                  {index === 0 && <SecurityIcon fontSize="large" />}
                  {index === 1 && <GppGoodIcon fontSize="large" />}
                  {index === 2 && <ExitToAppIcon fontSize="large" />}
                  {index === 3 && <WarningIcon fontSize="large" />}
                  {index === 4 && <ReportIcon fontSize="large" />}
                  {section.title}
                </Typography>
                <Divider
                  sx={{
                    width: '100px',
                    height: '4px',
                    backgroundColor: '#4551be',
                    mx: 'auto',
                    borderRadius: '2px'
                  }}
                />
              </Box>

              <Grid
                container
                spacing={6}
                alignItems="center"
                component={motion.div}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={staggerContainer}
                sx={{
                  maxWidth: '100%',
                  width: '100%',
                  margin: 0
                }}
              >
                {/* Image Column */}
                <Grid item xs={12} md={6} sx={{
                  width: '100%',
                  padding: { xs: '16px!important', md: '24px!important' }
                }}>
                  <Box
                    component={motion.div}
                    variants={index % 2 === 0 ? slideInLeft : slideInRight}
                    sx={{
                      position: 'relative',
                      borderRadius: 4,
                      overflow: 'hidden',
                      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
                      width: '100%',
                      margin: 0,
                      '&:hover img': {
                        transform: 'scale(1.05)',
                      }
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={`/images/${section.image}`}
                      alt={section.title}
                      sx={{
                        width: '100%',
                        height: { xs: 300, md: 400, lg: 450 },
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.1))',
                      }}
                    />
                  </Box>
                </Grid>

                {/* Content Column */}
                <Grid item xs={12} md={6} sx={{
                  width: '100%',
                  padding: { xs: '16px!important', md: '24px!important' }
                }}>
                  <Box
                    component={motion.div}
                    variants={index % 2 === 0 ? slideInRight : slideInLeft}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: '#475569',
                        mb: 3,
                        lineHeight: 1.6,
                        fontSize: {
                          xs: '1.1rem',
                          md: '1.25rem',
                          lg: '1.4rem'
                        }
                      }}
                    >
                      {section.description}
                    </Typography>

                    {/* Features List */}
                    {section.features && (
                      <List sx={{ mb: 4 }}>
                        {section.features.map((feature, idx) => (
                          <ListItem
                            key={idx}
                            component={motion.div}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }}
                            sx={{ py: 0.5 }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleIcon sx={{ color: '#4caf50' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{
                                sx: {
                                  fontSize: { xs: '0.95rem', md: '1rem' },
                                  color: '#64748b'
                                }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}

                    {/* Methods for Entry/Exit Gates */}
                    {(section.methods || section.cards || section.reportTypes) && (
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: '#4551be',
                            mb: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: { xs: '1.1rem', md: '1.25rem' }
                          }}
                        >
                          {section.methods && "Verification Methods"}
                          {section.cards && "Violation Cards"}
                          {section.reportTypes && "Report Types"}
                          <TimelineIcon />
                        </Typography>

                        {section.methods && (
                          <Grid container spacing={2} sx={{
                            maxWidth: '100%',
                            width: '100%',
                            margin: 0
                          }}>
                            {section.methods.map((method, idx) => (
                              <Grid item xs={12} sm={6} key={idx} sx={{
                                width: '100%',
                                padding: '8px!important'
                              }}>
                                <Card
                                  component={motion.div}
                                  whileHover={{ y: -5 }}
                                  variants={scaleIn}
                                  sx={{
                                    height: '100%',
                                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    width: '100%',
                                    margin: 0
                                  }}
                                >
                                  <CardContent sx={{ p: 2.5 }}>
                                    <Box sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      mb: 1.5,
                                      color: '#4551be'
                                    }}>
                                      {method.icon}
                                      <Typography variant="subtitle1" sx={{
                                        fontWeight: 700,
                                        ml: 1,
                                        fontSize: { xs: '0.95rem', md: '1rem' },
                                        color: '#1e293b'
                                      }}>
                                        {method.title}
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', color: '#64748b' }}>
                                      {method.description}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        )}

                        {section.cards && (
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ width: '100%' }}>
                            {section.cards.map((card, idx) => (
                              <Card
                                key={idx}
                                component={motion.div}
                                whileHover={{ scale: 1.05 }}
                                variants={scaleIn}
                                sx={{
                                  flex: 1,
                                  background: card.color === 'grey'
                                    ? 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)'
                                    : card.color === '#FFD700'
                                      ? 'linear-gradient(135deg, #fff9c4 0%, #ffd700 100%)'
                                      : 'linear-gradient(135deg, #ffcdd2 0%, #f44336 100%)',
                                  border: `3px solid ${card.color}`,
                                  borderRadius: 3,
                                  textAlign: 'center',
                                  p: 3,
                                  width: '100%',
                                  margin: 0
                                }}
                              >
                                <Typography variant="h5" sx={{
                                  fontWeight: 900,
                                  mb: 1,
                                  fontSize: { xs: '1.3rem', md: '1.5rem' },
                                  color: '#1e293b'
                                }}>
                                  {card.title}
                                </Typography>
                                <Typography variant="body2" sx={{
                                  color: '#64748b',
                                  fontSize: { xs: '0.85rem', md: '0.9rem' }
                                }}>
                                  {card.description}
                                </Typography>
                              </Card>
                            ))}
                          </Stack>
                        )}

                        {section.reportTypes && (
                          <Grid container spacing={2} sx={{
                            maxWidth: '100%',
                            width: '100%',
                            margin: 0
                          }}>
                            {section.reportTypes.map((report, idx) => (
                              <Grid item xs={12} sm={6} key={idx} sx={{
                                width: '100%',
                                padding: '8px!important'
                              }}>
                                <Chip
                                  component={motion.div}
                                  whileHover={{ scale: 1.05 }}
                                  label={report}
                                  sx={{
                                    width: '100%',
                                    height: 'auto',
                                    py: 1.5,
                                    fontSize: { xs: '0.85rem', md: '0.95rem' },
                                    background: 'linear-gradient(45deg, #e3f2fd 30%, #bbdefb 90%)',
                                    border: '1px solid #90caf9',
                                    color: '#1e293b'
                                  }}
                                  icon={<AnalyticsIcon />}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </Box>
                    )}

                    {/* Benefits for Reports Section */}
                    {section.benefits && (
                      <Box sx={{ mt: 4 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: '#4551be',
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: { xs: '1.1rem', md: '1.25rem' }
                          }}
                        >
                          Key Benefits
                          <AnalyticsIcon />
                        </Typography>
                        <Grid container spacing={2} sx={{
                          maxWidth: '100%',
                          width: '100%',
                          margin: 0
                        }}>
                          {section.benefits.map((benefit, idx) => (
                            <Grid item xs={12} key={idx} sx={{
                              width: '100%',
                              padding: '8px!important'
                            }}>
                              <Box
                                component={motion.div}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                sx={{
                                  p: 2,
                                  backgroundColor: '#e8f5e9',
                                  borderRadius: 2,
                                  borderLeft: '4px solid #4caf50',
                                  width: '100%',
                                  margin: 0
                                }}
                              >
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.9rem' }, color: '#1e293b' }}>
                                  {benefit}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Box>
      <HomeFooter />
    </Box>
  );
};

export default ICleverGate;